import { useRef, useState } from 'react';
import {
  Controller,
  FieldPath,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { CircleXIcon } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import { DatePicker } from 'components/DatePicker';
import InputPreviousValue from 'components/InputPreviousValue';
import { Label } from 'components/Label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Input } from 'components/input';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { useFieldAttributes } from 'hooks/useLoadContext';
import useTMSContext from 'hooks/useTMSContext';
import { LoadDateValue, NormalizedLoad } from 'types/Load';
import {
  FieldAttributes,
  getFieldAttribute,
  initFieldAttributes,
} from 'types/LoadAttributes';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { TMS, TMSTimezoneConfig } from 'types/enums/Integrations';
import { normalizeTimezone } from 'utils/normalizeTimezone';
import { processTimeAndUpdateDate } from 'utils/processTimeAndUpdateDate';
import { cn } from 'utils/shadcn';
import {
  formatLoadInstantForDisplay,
  getShortTimezoneAbbreviation,
} from 'utils/time';

type LoadDateTimeInputProps = Omit<
  React.ComponentPropsWithoutRef<typeof RHFTextInput>,
  'prevValue'
> & {
  name: FieldPath<NormalizedLoad>;
  showAIHint?: boolean;
  prevValue?: Undef<Date>;
  load?: NormalizedLoad;
  rules?: Omit<
    RegisterOptions<NormalizedLoad, FieldPath<NormalizedLoad>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  timeCanBeEmpty?: boolean;
  defaultTimezone?: Maybe<string>;
  defaultDate?: Maybe<LoadDateValue>;
  forceEditable?: boolean;
  readOnly?: boolean;
  disablePastDates?: boolean;
  timezoneOverride?: MaybeUndef<string>;
};

export const LoadDateTimeInput = ({
  name,
  label,
  prevValue,
  required,
  load,
  rules,
  readOnly = false,
  timeCanBeEmpty = false,
  defaultTimezone,
  defaultDate,
  forceEditable = false,
  disablePastDates = true,
  timezoneOverride = null,
}: LoadDateTimeInputProps) => {
  const { tmsName } = useTMSContext();

  // Get timezone from the appropriate location based on field name
  const getTimezoneForField = (): MaybeUndef<string> => {
    if (timezoneOverride) {
      return timezoneOverride;
    }
    if (!load) return undefined;

    // For Aljex and Relay, don't apply location timezones - display dates as is from BE (UTC)
    if (TMSTimezoneConfig[tmsName as TMS]?.isLoadTimezoneAgnostic) {
      return undefined;
    }

    // Extract the section name from the field path (e.g., 'pickup' from 'pickup.apptStartTime')
    const section = name.split('.')[0];

    let rawTimezone: MaybeUndef<string>;
    if (section === 'pickup' && load.pickup?.timezone) {
      rawTimezone = load.pickup.timezone;
    } else if (section === 'consignee' && load.consignee?.timezone) {
      rawTimezone = load.consignee.timezone;
    } else if (section === 'stops') {
      const stopIndex = Number(name.split('.')[1]);
      const stop = Number.isNaN(stopIndex)
        ? undefined
        : load.stops?.[stopIndex];
      rawTimezone = stop?.timezone || stop?.address?.timezone;
    } else if (section === 'carrier') {
      // For carrier fields, use pickup timezone as default
      rawTimezone = load.pickup?.timezone;
    } else {
      return undefined;
    }

    // Normalize the timezone to ensure it's in valid IANA format
    return normalizeTimezone(rawTimezone);
  };

  // Get the timezone string (e.g. "America/Chicago")
  const timezone = getTimezoneForField();

  // Normalize defaultTimezone as well
  const normalizedDefaultTimezone = defaultTimezone
    ? normalizeTimezone(defaultTimezone)
    : undefined;
  const effectiveTimezone = timezone ?? normalizedDefaultTimezone;
  const shortTZ = getShortTimezoneAbbreviation(
    effectiveTimezone,
    tmsName as TMS
  );
  const normalizedLabel = label + (shortTZ ? ` (${shortTZ})` : '');

  const [timeInput, setTimeInput] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const timeInputRef = useRef<HTMLInputElement>(null);

  const extendedFormContext = useExtendedFormContext();
  const formContext = useFormContext<NormalizedLoad>();
  const {
    control: formControl,
    getFieldState,
    clearErrors,
    setError,
    formState: { dirtyFields, errors },
  } = formContext;
  const posthog = usePostHog();

  // Format via shared helper — stop TZ when available, otherwise UTC
  const formatDateWithTimezone = (dateValue: any): string => {
    if (!dateValue) return '';
    return formatLoadInstantForDisplay(dateValue, {
      tmsName,
      stopTimezone: effectiveTimezone,
      format: 'HH:mm',
    });
  };

  // When selecting a date, ensure it's in the correct timezone
  const handleDateSelect = (date: Maybe<Date>, field: any) => {
    if (typeof field.onChange === 'function') {
      field.onChange(date);

      // Focus on time input after date selection
      if (date && timeInputRef.current) {
        setTimeout(() => timeInputRef.current?.focus());
      }
    }
  };

  const handleTimeInputBlur = (field: any) => {
    // Only process if the user has entered something
    if (timeInput.trim()) {
      const baseDate = field.value || defaultDate;

      // Match display: stop TZ when available, otherwise UTC
      const newDate = processTimeAndUpdateDate(
        timeInput,
        baseDate,
        effectiveTimezone || 'UTC'
      );

      if (newDate) {
        clearErrors(name);
        if (typeof field.onChange === 'function') {
          field.onChange(newDate);
        }
      } else {
        setError(name, {
          type: 'manual',
          message: 'Invalid time format. Please use HH:mm',
        });
      }
    } else if (timeCanBeEmpty) {
      if (!field.value) return;

      field.onChange(dayjs(field.value).format('YYYY-MM-DD'));
    } else {
      // If they didn't enter anything, don't show an error
      clearErrors(name);
    }

    setTimeInput('');
  };

  const highlightDirtyField =
    !!extendedFormContext &&
    (() => {
      const highlightDirtyFields =
        extendedFormContext.highlightDirtyFields ?? false;
      const fieldState = getFieldState(name, formContext.formState);
      const isDirty = fieldState.isDirty || get(dirtyFields, name);

      return Boolean(isDirty && highlightDirtyFields);
    })();

  const allFieldAttrs = useFieldAttributes();

  const getFieldAttrForName = (fieldName: string): FieldAttributes => {
    let fieldAttr = getFieldAttribute(allFieldAttrs, fieldName);

    // Check if field is a stops field with index
    if (fieldName.startsWith('stops.')) {
      const parts = fieldName.split('.');

      if (parts.length >= 3 && !isNaN(Number(parts[1]))) {
        // Remove the index part (e.g., stops.1.pickup -> stops.pickup)
        const fieldNameWithoutIndex = `${parts[0]}.${parts.slice(2).join('.')}`;
        fieldAttr = getFieldAttribute(allFieldAttrs, fieldNameWithoutIndex);
      }
    }

    return fieldAttr ?? initFieldAttributes;
  };

  const baseFieldAttr: FieldAttributes = getFieldAttrForName(name);
  const thisFieldAttr = forceEditable
    ? { ...baseFieldAttr, isReadOnly: false }
    : baseFieldAttr;

  const prevValueStr = prevValue
    ? formatLoadInstantForDisplay(prevValue, {
        tmsName,
        stopTimezone: effectiveTimezone,
        format: 'M/DD/YYYY HH:mm',
      })
    : '';

  // Disable dates before today
  const disabledDates = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return thisFieldAttr.isNotSupported ? null : (
    <div className='w-full'>
      <Label name={name} required={required}>
        {normalizedLabel}
      </Label>

      <Controller
        name={name}
        control={formControl}
        rules={required ? { ...rules, required: 'Required' } : rules}
        render={({ field }) => {
          // Format display time with the appropriate timezone
          const displayTime = field.value
            ? formatDateWithTimezone(field.value)
            : '';

          return (
            <TooltipProvider>
              <Flex gap='sm' className='mt-1 w-full min-w-0' data-name={name}>
                <Flex gap='lg' className='min-w-0 flex-1'>
                  <div className='min-w-0 flex-1'>
                    <DatePicker
                      logProperties={{ dateLabel: label }}
                      field={{
                        ...field,
                        onChange: (date: Maybe<Date>) =>
                          handleDateSelect(date, field),
                      }}
                      thisFieldAttr={thisFieldAttr}
                      highlightDirtyField={highlightDirtyField}
                      disabled={disablePastDates ? disabledDates : false}
                      timezone={effectiveTimezone || 'UTC'}
                      readOnly={readOnly || thisFieldAttr.isReadOnly}
                      className='w-full'
                    />
                  </div>

                  <Input
                    ref={timeInputRef}
                    type='text'
                    className={cn(
                      'h-8 w-20 shrink-0 rounded-[4px] border border-neutral-input-border bg-neutral-input-bg text-neutral-input-text pr-1! pl-3',
                      highlightDirtyField && 'bg-warning-200'
                    )}
                    placeholder='HH:mm'
                    value={isEditing ? timeInput : timeInput || displayTime}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setTimeInput(e.target.value);
                      setIsEditing(true);
                      posthog.capture('load_building_time_input_change', {
                        timeInput: e.target.value,
                        field: name,
                      });
                    }}
                    onFocus={() => setIsEditing(true)}
                    onBlur={() => {
                      handleTimeInputBlur(field);
                      setIsEditing(false);
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        // Trigger blur to process the time input
                        timeInputRef.current?.blur();
                      }
                    }}
                    readOnly={readOnly || thisFieldAttr.isReadOnly || readOnly} // Hides placeholders
                    disabled={readOnly || thisFieldAttr.isReadOnly || readOnly} // Allows selecting the value
                  />
                </Flex>
                {field.value && !thisFieldAttr.isReadOnly && !readOnly && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        title='Clear date'
                        onClick={() => field.onChange(null)}
                        className='flex h-9 shrink-0 items-center justify-center'
                      >
                        <CircleXIcon className='h-4 w-4' />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Clear date</TooltipContent>
                  </Tooltip>
                )}
              </Flex>
            </TooltipProvider>
          );
        }}
      />

      {prevValue ? <InputPreviousValue prevValue={prevValueStr} /> : null}

      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }: { message: string }) => (
          <Typography variant='body-xs' className='text-error-500'>
            {message}
          </Typography>
        )}
      />
    </div>
  );
};
